import { OFFERED_HAIRSTYLES } from './appearance';

/**
 * The style chair's colour bar: a curated set of shades per part, named the
 * way the colour bar in a salon would name them. Deliberately separate from
 * the service palettes in shared/ColourPicker, which are tuned for keeping
 * diary text readable rather than for hair, polish or makeup.
 *
 * `group` splits a rail into rows. The first shade of each part that the
 * first version used as its default is kept, so saved looks still match a
 * swatch.
 */
export const HAIR_SHADES = [
    { hex: '#1c1a1a', name: 'Jet black', group: 'Naturals' },
    { hex: '#302820', name: 'Espresso', group: 'Naturals' },
    { hex: '#4a3222', name: 'Chocolate', group: 'Naturals' },
    { hex: '#6d4028', name: 'Chestnut', group: 'Naturals' },
    { hex: '#8a3a23', name: 'Auburn', group: 'Naturals' },
    { hex: '#b8612f', name: 'Copper', group: 'Naturals' },
    { hex: '#a8753f', name: 'Caramel', group: 'Naturals' },
    { hex: '#c89a55', name: 'Honey', group: 'Naturals' },
    { hex: '#dcb56d', name: 'Golden blonde', group: 'Naturals' },
    { hex: '#cdbd9e', name: 'Ash blonde', group: 'Naturals' },
    { hex: '#ece6d8', name: 'Platinum', group: 'Naturals' },
    { hex: '#b9b8b6', name: 'Silver', group: 'Naturals' },
    { hex: '#df9baf', name: 'Candy pink', group: 'Fashion shades' },
    { hex: '#d8a08d', name: 'Rose gold', group: 'Fashion shades' },
    { hex: '#a899c6', name: 'Lilac', group: 'Fashion shades' },
    { hex: '#6a4fa3', name: 'Violet', group: 'Fashion shades' },
    { hex: '#1f7e8c', name: 'Peacock', group: 'Fashion shades' },
    { hex: '#3a6fc1', name: 'Ocean blue', group: 'Fashion shades' },
    { hex: '#8fd3b6', name: 'Mint', group: 'Fashion shades' },
    { hex: '#c8292a', name: 'Pillar-box red', group: 'Fashion shades' },
    { hex: '#ee7a3a', name: 'Sunset orange', group: 'Fashion shades' },
    { hex: '#f2d65c', name: 'Lemon', group: 'Fashion shades' }
];

// Beu's own colours first, then the rest of the rail.
export const TEE_COLOURS = [
    { hex: '#29282d', name: 'Beu charcoal', group: 'Beu' },
    { hex: '#007ea0', name: 'Beu teal', group: 'Beu' },
    { hex: '#f6f3ee', name: 'Chalk', group: 'Beu' },
    { hex: '#d8cfc2', name: 'Oat', group: 'Beu' },
    { hex: '#2b3a55', name: 'Navy', group: 'Colours' },
    { hex: '#2f5d4a', name: 'Forest', group: 'Colours' },
    { hex: '#8e3b5a', name: 'Berry', group: 'Colours' },
    { hex: '#c23b33', name: 'Red', group: 'Colours' },
    { hex: '#ec7d63', name: 'Coral', group: 'Colours' },
    { hex: '#f3dc8a', name: 'Butter', group: 'Colours' },
    { hex: '#9cc7e0', name: 'Sky', group: 'Colours' },
    { hex: '#b9a8d8', name: 'Lilac', group: 'Colours' },
    { hex: '#a8c6a0', name: 'Sage', group: 'Colours' },
    { hex: '#f2c1cc', name: 'Blossom', group: 'Colours' }
];

export const NAIL_COLOURS = [
    { hex: '#d9b8a3', name: 'Nude' },
    { hex: '#eab3bd', name: 'Ballet pink' },
    { hex: '#b3202a', name: 'Classic red' },
    { hex: '#9d435c', name: 'Berry' },
    { hex: '#5b2345', name: 'Plum' },
    { hex: '#f06c5b', name: 'Coral' },
    { hex: '#b7a3e0', name: 'Lilac' },
    { hex: '#8ec5e8', name: 'Sky' },
    { hex: '#9fd9c0', name: 'Mint' },
    { hex: '#f1d86f', name: 'Butter' },
    { hex: '#1d1b1f', name: 'Black' },
    { hex: '#f7f4ef', name: 'White' }
];

export const LINER_COLOURS = [
    { hex: '#1f1d22', name: 'Black' },
    { hex: '#5a3b2c', name: 'Brown' },
    { hex: '#22335e', name: 'Navy' },
    { hex: '#425ed1', name: 'Cobalt' },
    { hex: '#0f7c86', name: 'Teal' },
    { hex: '#6b3fa0', name: 'Violet' },
    { hex: '#1f7a4d', name: 'Emerald' },
    { hex: '#c9a24a', name: 'Gold' }
];

export const LIP_COLOURS = [
    { hex: '#c58f7c', name: 'Nude' },
    { hex: '#c96a7d', name: 'Rose' },
    { hex: '#b3202a', name: 'Classic red' },
    { hex: '#9d435c', name: 'Berry' },
    { hex: '#6b2748', name: 'Plum' },
    { hex: '#e46b5d', name: 'Coral' },
    { hex: '#eea07f', name: 'Peach' },
    { hex: '#9a6e84', name: 'Mauve' }
];

export const BLUSH_COLOURS = [
    { hex: '#f0a98c', name: 'Peach' },
    { hex: '#d69998', name: 'Rose' },
    { hex: '#eb9fb3', name: 'Pink' },
    { hex: '#c77a8f', name: 'Berry' },
    { hex: '#eaa56d', name: 'Apricot' },
    { hex: '#b8805f', name: 'Bronze' }
];

export const shadeName = (list, hex) => {
    const found = list.find(s => s.hex === String(hex).toLowerCase());
    return found ? found.name : 'Your own mix';
};

const pickFrom = (list, random) => list[Math.floor(random() * list.length) % list.length];
const weighted = (pairs, random) => {
    let roll = random() * pairs.reduce((sum, [, w]) => sum + w, 0);
    for (let i = 0; i < pairs.length; i++) { roll -= pairs[i][1]; if (roll <= 0) return pairs[i][0]; }
    return pairs[pairs.length - 1][0];
};

/**
 * "Surprise me": a random look that still looks put together. Hair is mostly
 * a cut, its second colour comes from the same family as the first, and
 * makeup and nails are sometimes left off. `extras` allows technique,
 * finish and tattoos.
 */
export function surpriseLook(current, extras, random = Math.random) {
    const naturals = HAIR_SHADES.filter(s => s.group === 'Naturals');
    const fashion = HAIR_SHADES.filter(s => s.group !== 'Naturals');
    for (let attempt = 0; attempt < 6; attempt++) {
        const family = random() < 0.62 ? naturals : fashion;
        const hairColour = pickFrom(family, random).hex;
        const accentFamily = random() < 0.7 ? family : (family === naturals ? fashion : naturals);
        const makeupOn = random() < 0.5;
        const look = {
            ...current,
            hairStyle: random() < 0.85 ? pickFrom(extras ? OFFERED_HAIRSTYLES.filter(h => h.id !== 'none').map(h => h.id) : ['fade', 'curls', 'bob'], random) : 'none',
            hairColour,
            hairAccent: pickFrom(accentFamily.filter(s => s.hex !== hairColour), random).hex,
            shirtColour: pickFrom(TEE_COLOURS, random).hex,
            nailsEnabled: random() < 0.6,
            nailColour: pickFrom(NAIL_COLOURS, random).hex,
            makeupEnabled: makeupOn,
            linerColour: pickFrom(LINER_COLOURS, random).hex,
            lipColour: pickFrom(LIP_COLOURS, random).hex,
            blushColour: pickFrom(BLUSH_COLOURS, random).hex
        };
        if (extras) {
            look.hairTechnique = weighted([['solid', 4], ['highlights', 2.5], ['balayage', 2], ['tips', 1.5]], random);
            look.linerOn = random() < 0.8; look.lipsOn = random() < 0.85; look.blushOn = random() < 0.7;
            const designs = ['scissors', 'comb', 'rose', 'mum', 'swallow', 'star', 'moon', 'lightning', 'butterfly', 'anchor', 'vine', 'dots'];
            const roll = random();
            look.tattooLeft = roll < 0.4 ? pickFrom(designs, random) : 'none';
            look.tattooRight = roll < 0.15 || (roll >= 0.4 && roll < 0.55) ? pickFrom(designs, random) : 'none';
            look.tattooInk = weighted([['black', 3], ['fineline', 1.5], ['colour', 2]], random);
        } else {
            look.hairTechnique = 'highlights';
        }
        if (look.hairStyle !== current.hairStyle || look.hairColour !== current.hairColour || look.shirtColour !== current.shirtColour) return look;
    }
    return { ...current, shirtColour: TEE_COLOURS.find(t => t.hex !== current.shirtColour).hex };
}
